'use client';
import { useState } from 'react';
import { LogImpression, Spacing, useOverlay } from '~/components';
import { Button, IcBookmarkFill, Typography, Snackbar, BottomSheet } from '@bgzt/ui';
import { useIsMounted } from '@bgzt/hooks';
import { utilStyles } from '~/styles';
import IcCoupon from '~/public/assets/icons/ic_coupon.svg';
import IcCouponDownload from '~/public/assets/icons/ic_coupon_download.svg';

import { ProductMercariDetailModel } from '~/apis/pms';
import { useMutation } from '@tanstack/react-query';
import { usePostCoupon } from '~/app/products/[pid]/_queries';
import { IssuableCoupon } from '~/app/products/[pid]/_components/IssuableCoupon';
import { togglePullToRefresh } from '~/services/app-interface/toggle-pull-to-refresh';
import { authStorage } from '~/services/auth';
import { Coupon, IssuableCouponsParams, getIssuableCoupons } from '~/apis/coupon';
import { EVENT_ACTION, BUTTON_ID, EVENT_TYPE, PAGE_ID, VIEW_ID } from '~/apis/log/constant';
import { useSendLog } from '~/hooks';

import styles from './Summary.module.css';

type Props = {
  data: ProductMercariDetailModel;
};

export const Summary = ({ data: productData }: Props) => {
  const token = authStorage.getToken();
  const overlay = useOverlay();
  const [isIssuableCouponListOpen, setIsIssuableCouponListOpen] = useState(false);
  const [coupons, setCoupons] = useState<Coupon[]>([]);
  const sendLog = useSendLog();

  const params: IssuableCouponsParams = {
    pid: Number(productData.product.pid),
    paymentAmount: productData.product.price,
    categoryId: productData.product.category.id,
    sellerUid: productData.shop.uid,
    overseas: true,
    productInspectionStatus: 'NON_TARGET',
  };

  const { mutateAsync: mutateGetCoupons } = useMutation({
    mutationFn: (params: IssuableCouponsParams) => getIssuableCoupons(params),
  });

  const { mutateAsync: mutatePostCoupon } = usePostCoupon({
    onError: async (error) => {
      await showSnackbar(error.reason ?? '쿠폰 다운로드에 실패했어요.');
    },
  });

  const showSnackbar = async (message: string) => {
    await overlay.open(({ isOpen, close }) => (
      <Snackbar
        isOpen={isOpen}
        onClose={() => {
          close();
        }}
        bottomOffsetInfo={{ defaultOffset: 82 + 6 }} // bottomSheet (dragable 옵션 설정시) + 스낵바의 zindex 의 위계 조정이 어려워 이와같이 수정
      >
        {message}
      </Snackbar>
    ));
  };

  const handleCouponTextClick = async () => {
    if (!token) return;

    // select 로그
    sendLog({
      event_type: EVENT_TYPE.SELECT,
      event_action: EVENT_ACTION.SELECT_BUTTON,
      page_id: PAGE_ID.PRODUCT,
      button_id: BUTTON_ID.COUPON_DOWNLOAD,
      page_label: productData.product.pid.toString(),
    });
    // visit log
    sendLog({
      event_type: EVENT_TYPE.VISIT,
      event_action: EVENT_ACTION.VISIT,
      page_id: PAGE_ID.PRODUCT_COUPON_DOWNLOAD_BOTTOM_SHEET,
      page_label: productData.product.pid.toString(),
    });

    const { data: mutateCouponsData } = await mutateGetCoupons(params);
    if (mutateCouponsData && mutateCouponsData.length < 1) {
      await showSnackbar('발급 기간이 지나 받을 수 있는 쿠폰이 없어요. ');
      return;
    }
    setCoupons(mutateCouponsData);
    setIsIssuableCouponListOpen(true);
    togglePullToRefresh(false); // bottom sheet 가 열리면 PTR 금지
  };
  const handleCloseIssuableCouponList = () => {
    setIsIssuableCouponListOpen(false);
    togglePullToRefresh(true); // bottom sheet 가 닫히면 PTR 허용
  };
  const handleClickCoupon = async (inputCoupon: Coupon) => {
    if (!inputCoupon.downloadable) return;

    // 로깅
    sendLog({
      event_type: EVENT_TYPE.SELECT,
      event_action: EVENT_ACTION.SELECT_BUTTON,
      page_id: PAGE_ID.PRODUCT_COUPON_DOWNLOAD_BOTTOM_SHEET,
      button_id: BUTTON_ID.COUPON,
      page_label: productData.product.pid.toString(),
      content_id: inputCoupon.couponId.toString(),
    });

    try {
      await mutatePostCoupon(inputCoupon.couponId);
      await showSnackbar('쿠폰을 성공적으로 받았어요.');
      // update coupons
      const newCoupons = coupons.map((c) => ({
        ...c,
        downloadable: c.couponId === inputCoupon.couponId ? false : c.downloadable,
      }));
      setCoupons(newCoupons);
    } catch (error) {
      console.log(error);
    }
  };

  const isMounted = useIsMounted();

  return (
    <>
      <div className={styles.wrapper}>
        {/* 상품명 */}
        <Typography variant="B1" as="p" color="gray-900" style={{ paddingRight: 70 }}>
          {productData.product.name}
        </Typography>

        {/* 원가 */}
        {productData.product.originPrice && productData.product.discount && (
          <>
            <Spacing height={8} />
            <Typography variant="L8" as="p" color="gray-300" style={{ textDecoration: 'line-through' }}>
              {productData.product.originPrice.toLocaleString()}원
            </Typography>
          </>
        )}

        <Spacing height={4} />

        {/* 할인율 + 판매가격 */}
        <Typography variant="T4" as="p" color="gray-900">
          {productData.product.discount && (
            <Typography variant="T4" as="strong" color="red-500" style={{ marginRight: 4 }}>
              {productData.product.discount.rate}%
            </Typography>
          )}
          {productData.product.price.toLocaleString()}원
        </Typography>

        <Spacing height={4} />

        {/* 설명 */}
        <Typography variant={'C2'} color="gray-300">
          해외 배송비,관부가세가 모두 포함된 가격
        </Typography>

        {/* 쿠폰 텍스트 버튼 */}
        {productData.couponText && (
          <>
            <Spacing height={14} />
            <LogImpression
              params={{
                event_type: EVENT_TYPE.EXPOSURE,
                event_action: EVENT_ACTION.EXPOSURE_BANNER,
                page_id: PAGE_ID.PRODUCT,
                view_id: VIEW_ID.DOWNLOAD_COUPON,
                page_label: productData.product.pid.toString(),
              }}
            >
              <button className={styles.couponTextButton} onClick={handleCouponTextClick}>
                <div className={styles.couponText}>
                  <IcCoupon />
                  <Typography variant={'L9'} color="gray-800">
                    {productData.couponText}
                  </Typography>
                </div>
                <IcCouponDownload />
              </button>
            </LogImpression>
          </>
        )}

        <Spacing height={14} />

        {/* 지역 + 업데이트 + 좋아요 정보 */}
        <div className={styles.additionals}>
          <Typography variant="L12" as="p">
            {productData.product.location}・{productData.product.updatedBefore}
          </Typography>
          <Typography variant="L12" as="p" className={utilStyles.iconText}>
            <IcBookmarkFill size={12} fill={'#e5e5e5'} />
            {productData.product.metric.favoriteCount.toLocaleString()}
          </Typography>
        </div>
      </div>

      {/* 쿠폰 다운로드 바텀시트 */}
      {/* BottomSheet 컴포넌트의 hydration mismatch 이슈 때문에 client side 에서만 렌더링 되도록 조건 추가 */}
      {isMounted && (
        <BottomSheet isOpen={isIssuableCouponListOpen} onClose={handleCloseIssuableCouponList} draggable>
          <Typography variant="T4" color="gray-900">
            쿠폰받기
          </Typography>
          <Spacing height={20} />
          <ul className={styles.couponList}>
            {coupons?.map((coupon) => (
              <li key={coupon.couponId} onClick={() => handleClickCoupon(coupon)}>
                <IssuableCoupon coupon={coupon} />
              </li>
            ))}
          </ul>

          <BottomSheet.Footer className={styles.couponBottomSheetFooter}>
            <Button width={'100%'} size="xlarge" variant="normalGray" onClick={handleCloseIssuableCouponList}>
              확인
            </Button>
          </BottomSheet.Footer>
        </BottomSheet>
      )}
    </>
  );
};
